import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { getClientTypeLabel, resolvePriceClientType } from "../utils/clientType";
import {
  requestPriceListPdfJob,
  getPriceListPdfJobStatus,
  downloadPriceListPdfJob,
  buildPublicPriceListPdfUrl,
  getProducts,
  getPriceRecommendationSettings,
  getReferenceCosts,
  markPriceAsAvailable,
  markPriceAsSold,
  updateVarietyPrice,
} from "../api";
import CategoryFilterBar from "../components/CategoryFilterBar";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import usePurchaseEvents from '../hooks/usePurchaseEvents';
import {
  areAllPricesSoldOut,
  getAvailabilityLabelForPrices,
} from '../utils/productAvailability';
import {
  DEFAULT_PRICE_RECOMMENDATION_SETTINGS,
  calculateStockRulePrice,
  normalizePriceRecommendationSettings,
} from '../utils/stockPriceRule';

const FALLBACK_IMAGE = "https://placehold.co/60";
const clientTypeOrder = {
  detalle: 1,
  mayorista: 2,
  importador: 3,
};

const resolveClientTypeOrder = (value) => clientTypeOrder[value] || 99;

const normalizePriceValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const normalizeName = (value) => String(value || '').trim();
const stripVarietyPrefix = (productName, varietyName) => {
  const productLabel = normalizeName(productName);
  const varietyLabel = normalizeName(varietyName);
  if (!productLabel || !varietyLabel) return varietyLabel;
  const productLower = productLabel.toLowerCase();
  const varietyLower = varietyLabel.toLowerCase();
  if (!varietyLower.startsWith(productLower)) return varietyLabel;
  let stripped = varietyLabel.slice(productLabel.length).trim();
  stripped = stripped.replace(/^[-–—:|/]+\\s*/, '');
  return stripped || varietyLabel;
};

const normalizeCategoryKey = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const CLIENT_TYPE_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "mayorista", label: "Mayorista" },
  { id: "detalle", label: "Detalle" },
  { id: "importador", label: "Importacion" },
];

const getDefaultPdfIncludeSoldOut = (clientType) => clientType === "importador";

const createInitialPdfModalState = () => ({
  isOpen: false,
  search: "",
  clientType: "all",
  categoryIds: [],
  includeAllCategories: true,
  includeSoldOut: false,
  error: "",
  shareUrl: "",
  downloadUrl: "",
  jobId: "",
  copyStatus: "",
});

const formatPriceLabel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `$${parsed.toLocaleString("de-DE")}`;
};

const formatDateLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const getAllocationBasisLabel = (value) => {
  if (value === "weight") return "peso";
  if (value === "quantity") return "cantidad";
  return "";
};

const buildReferenceCostTitle = (referenceCost) => {
  if (!referenceCost) return "";
  const parts = [];
  if (referenceCost.sourceStockListName) {
    parts.push(`Lista: ${referenceCost.sourceStockListName}`);
  }
  const dateLabel = formatDateLabel(referenceCost.sourceStockListCreatedAt);
  if (dateLabel) parts.push(`Fecha: ${dateLabel}`);
  const basisLabel = getAllocationBasisLabel(referenceCost.allocationBasis);
  if (basisLabel) parts.push(`Prorrateo por ${basisLabel}`);
  if (referenceCost.conversionStatus === "missing_unit_weight") {
    parts.push("Falta peso de unidad para convertir a este precio");
  }
  return parts.join(" · ");
};

const renderReferenceCostLabel = (referenceCost) => {
  if (!referenceCost) {
    return <span className="text-gray-400">-</span>;
  }
  if (referenceCost.conversionStatus === "missing_unit_weight") {
    return <span className="text-xs font-semibold text-amber-700">Falta peso</span>;
  }
  if (referenceCost.referenceCost === null || referenceCost.referenceCost === undefined) {
    return <span className="text-gray-400">-</span>;
  }
  return <span>{formatPriceLabel(referenceCost.referenceCost)}</span>;
};

const buildVariantGroupKey = (product, variety) => {
  const productKey = product?.id ?? product?.productName ?? "product";
  const varietyKey = variety?.id ?? variety?.varietyName ?? "variety";
  return `${productKey}:${varietyKey}`;
};

const buildProductGroupKey = (product) => {
  return String(product?.id ?? product?.productName ?? "product");
};

const getUniqueLabels = (values) => {
  const labels = values
    .map((value) => normalizeName(value))
    .filter(Boolean);
  return Array.from(new Set(labels));
};

const getVariantAvailabilityLabel = (priceRows, soldOutLabel = "Agotada") =>
  getAvailabilityLabelForPrices(priceRows.map((row) => row.price), soldOutLabel);

const getVariantAvailabilityClass = (label) => {
  if (label === "Agotada" || label === "Agotado") return "bg-red-100 text-red-700 border-red-200";
  if (label === "Parcial") return "bg-amber-100 text-amber-800 border-amber-200";
  if (label === "Sin precios") return "bg-gray-100 text-gray-600 border-gray-200";
  return "bg-emerald-100 text-emerald-700 border-emerald-200";
};

const AdminPriceListPage = ({ embedded = false, onOpenFullPage }) => {
  const { branchId: branchIdParam } = useParams();
  const branchId = branchIdParam || localStorage.getItem("selectedBranchId");
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const canReadProducts = hasPermission(user, "product.read");
  const canUpdateProducts = hasPermission(user, "product.update");
  const [products, setProducts] = useState([]);
  const [referenceCostsByPriceId, setReferenceCostsByPriceId] = useState({});
  const [priceRecommendationSettings, setPriceRecommendationSettings] = useState(DEFAULT_PRICE_RECOMMENDATION_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draftPrices, setDraftPrices] = useState({});
  const [savingPrices, setSavingPrices] = useState({});
  const [savingSoldOut, setSavingSoldOut] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfModal, setPdfModal] = useState(createInitialPdfModalState);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedClientType, setSelectedClientType] = useState("all");
  const [expandedProductKeys, setExpandedProductKeys] = useState({});
  const [expandedVariantKeys, setExpandedVariantKeys] = useState({});
  const [activeSaveCount, setActiveSaveCount] = useState(0);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const saveCountRef = useRef(0);
  const savedToastTimer = useRef(null);
  const pendingPdfJobRef = useRef(null);
  const pdfPollTimerRef = useRef(null);
  const pdfPollAttemptsRef = useRef(0);

  const clearSavedToastTimer = () => {
    if (savedToastTimer.current) {
      clearTimeout(savedToastTimer.current);
      savedToastTimer.current = null;
    }
  };

  const triggerSavedToast = () => {
    clearSavedToastTimer();
    setShowSavedToast(true);
    savedToastTimer.current = setTimeout(() => {
      setShowSavedToast(false);
    }, 2500);
  };

  useEffect(() => {
    return () => {
      clearSavedToastTimer();
      if (pdfPollTimerRef.current) {
        clearInterval(pdfPollTimerRef.current);
        pdfPollTimerRef.current = null;
      }
    };
  }, []);

  const clearPendingPdfJob = useCallback(() => {
    pendingPdfJobRef.current = null;
    if (pdfPollTimerRef.current) {
      clearInterval(pdfPollTimerRef.current);
      pdfPollTimerRef.current = null;
    }
    pdfPollAttemptsRef.current = 0;
  }, []);

  const downloadPdfByJob = useCallback(async (jobId) => {
    if (!jobId || !branchId) return;
    try {
      const blob = await downloadPriceListPdfJob(branchId, jobId);
      const fileName = `lista_precios_${branchId || "sucursal"}.pdf`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const shareUrl = buildPublicPriceListPdfUrl(branchId, jobId);
      const downloadUrl = buildPublicPriceListPdfUrl(branchId, jobId, { download: true });
      setPdfModal((prev) => ({
        ...prev,
        jobId,
        shareUrl,
        downloadUrl,
        error: "",
        copyStatus: "",
      }));
    } catch (err) {
      console.error("Error downloading PDF:", err);
      setError("No se pudo generar el PDF.");
      setPdfModal((prev) => ({
        ...prev,
        error: "No se pudo generar el PDF.",
      }));
    } finally {
      setPdfLoading(false);
      clearPendingPdfJob();
    }
  }, [branchId, clearPendingPdfJob]);

  const startPdfPolling = useCallback((jobId) => {
    if (!jobId || !branchId) return;
    if (pdfPollTimerRef.current) {
      clearInterval(pdfPollTimerRef.current);
    }
    pdfPollAttemptsRef.current = 0;
    pdfPollTimerRef.current = setInterval(async () => {
      if (!pendingPdfJobRef.current || pendingPdfJobRef.current !== jobId) {
        clearPendingPdfJob();
        return;
      }
      pdfPollAttemptsRef.current += 1;
      try {
        const status = await getPriceListPdfJobStatus(branchId, jobId);
        if (status?.status === "ready") {
          await downloadPdfByJob(jobId);
          return;
        }
        if (status?.status === "error") {
          setError("No se pudo generar el PDF.");
          setPdfLoading(false);
          clearPendingPdfJob();
          return;
        }
      } catch (err) {
        console.error("Error polling PDF job:", err);
      }
      if (pdfPollAttemptsRef.current >= 30) {
        setError("El PDF está tardando demasiado. Intenta nuevamente.");
        setPdfLoading(false);
        clearPendingPdfJob();
      }
    }, 4000);
  }, [branchId, clearPendingPdfJob, downloadPdfByJob]);

  const fetchProducts = useCallback(async () => {
    if (!branchId || !canReadProducts) return;
    setLoading(true);
    setError("");
    try {
      const [data, referenceCostData, recommendationSettingsData] = await Promise.all([
        getProducts(branchId, { pageSize: 0 }),
        getReferenceCosts(branchId).catch((err) => {
          console.error("Error fetching reference costs:", err);
          return { data: [] };
        }),
        getPriceRecommendationSettings(branchId).catch((err) => {
          console.error("Error fetching price recommendation settings:", err);
          return DEFAULT_PRICE_RECOMMENDATION_SETTINGS;
        }),
      ]);
      const normalized = normalizePaginatedResponse(data);
      const nextReferenceCosts = {};
      (referenceCostData?.data || []).forEach((entry) => {
        if (entry?.varietyPriceId === null || entry?.varietyPriceId === undefined) return;
        nextReferenceCosts[entry.varietyPriceId] = entry;
      });
      setProducts(normalized.data || []);
      setReferenceCostsByPriceId(nextReferenceCosts);
      setPriceRecommendationSettings(normalizePriceRecommendationSettings(recommendationSettingsData));
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("No se pudieron cargar los productos.");
    } finally {
      setLoading(false);
    }
  }, [branchId, canReadProducts]);

  useEffect(() => {
    if (!canReadProducts) {
      setLoading(false);
      setError("No tienes permisos para ver esta seccion.");
      return;
    }
    if (!branchId) {
      setLoading(false);
      setError("Selecciona una sucursal para ver la lista de precios.");
      return;
    }
    fetchProducts();
  }, [branchId, canReadProducts, fetchProducts]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      fetchProducts();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, fetchProducts]);

  useProductEvents(branchId, handleProductEvent);

  const handleBranchEvent = useCallback((data) => {
    if (!data) return;
    if (data.type === "price-recommendation-run.completed" && data.autoUpdatePrices !== false) {
      fetchProducts();
      return;
    }
    if (!pendingPdfJobRef.current) return;
    if (data.type === "price-list-pdf-ready" && data.jobId === pendingPdfJobRef.current) {
      downloadPdfByJob(data.jobId);
      return;
    }
    if (data.type === "price-list-pdf-error" && data.jobId === pendingPdfJobRef.current) {
      setError("No se pudo generar el PDF.");
      setPdfLoading(false);
      clearPendingPdfJob();
    }
  }, [clearPendingPdfJob, downloadPdfByJob, fetchProducts]);

  usePurchaseEvents(branchId, handleBranchEvent, { enabled: !!branchId });

  const productSoldOutMap = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const prices = (product.Varieties || []).flatMap((variety) => variety.VarietyPrices || []);
      map.set(product.id, areAllPricesSoldOut(prices));
    });
    return map;
  }, [products]);

  const rows = useMemo(() => {
    const items = [];
    products.forEach((product) => {
      const productSoldOut = productSoldOutMap.get(product.id);
      (product.Varieties || []).forEach((variety) => {
        (variety.VarietyPrices || []).forEach((price) => {
          const clientType = resolvePriceClientType(price);
          items.push({
            key: price.id,
            product,
            variety,
            price,
            referenceCost: referenceCostsByPriceId[price.id] || null,
            clientType,
            clientTypeLabel: getClientTypeLabel(clientType),
            productSoldOut,
            categoryId: normalizeCategoryKey(product?.categoryId ?? product?.Category?.id),
          });
        });
      });
    });
    return items.sort((a, b) => {
      const productCompare = a.product.productName.localeCompare(b.product.productName);
      if (productCompare !== 0) return productCompare;
      const varietyCompare = a.variety.varietyName.localeCompare(b.variety.varietyName);
      if (varietyCompare !== 0) return varietyCompare;
      return resolveClientTypeOrder(a.clientType) - resolveClientTypeOrder(b.clientType);
    });
  }, [products, productSoldOutMap, referenceCostsByPriceId]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const categoryId = product?.categoryId ?? product?.Category?.id;
      const categoryLabel =
        product?.Category?.name ||
        product?.categoryName ||
        product?.categoryLabel ||
        (categoryId !== undefined && categoryId !== null ? `Categoria ${categoryId}` : null);
      const resolvedId = normalizeCategoryKey(categoryId ?? categoryLabel);
      const resolvedLabel = normalizeCategoryKey(categoryLabel) || resolvedId;
      if (!resolvedId || !resolvedLabel) return;
      if (!map.has(resolvedId)) {
        map.set(resolvedId, {
          id: resolvedId,
          label: resolvedLabel,
          initial: String(resolvedLabel).trim().charAt(0).toUpperCase(),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [products]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const categoryFilter = selectedCategory !== "all" ? String(selectedCategory) : null;
    const clientTypeFilter = selectedClientType !== "all" ? String(selectedClientType) : null;
    return rows.filter((row) => {
      if (categoryFilter && String(row.categoryId) !== categoryFilter) return false;
      if (clientTypeFilter && row.clientType !== clientTypeFilter) return false;
      if (normalizedSearch) {
        const productName = String(row.product?.productName || "").toLowerCase();
        const varietyName = String(row.variety?.varietyName || "").toLowerCase();
        if (!productName.includes(normalizedSearch) && !varietyName.includes(normalizedSearch)) {
          return false;
        }
      }
      return true;
    });
  }, [rows, searchTerm, selectedCategory, selectedClientType]);

  const filteredVariantGroups = useMemo(() => {
    const groupMap = new Map();
    filteredRows.forEach((row) => {
      const groupKey = buildVariantGroupKey(row.product, row.variety);
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          key: groupKey,
          product: row.product,
          variety: row.variety,
          priceRows: [],
          productSoldOut: row.productSoldOut,
        });
      }
      groupMap.get(groupKey).priceRows.push(row);
    });
    return Array.from(groupMap.values());
  }, [filteredRows]);

  const filteredProductGroups = useMemo(() => {
    const groupMap = new Map();
    filteredVariantGroups.forEach((variantGroup) => {
      const groupKey = buildProductGroupKey(variantGroup.product);
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          key: groupKey,
          product: variantGroup.product,
          variantGroups: [],
          priceRows: [],
          productSoldOut: variantGroup.productSoldOut,
        });
      }
      const productGroup = groupMap.get(groupKey);
      productGroup.variantGroups.push(variantGroup);
      productGroup.priceRows.push(...variantGroup.priceRows);
    });
    return Array.from(groupMap.values());
  }, [filteredVariantGroups]);

  const getPriceRecommendationForRow = useCallback((row) => {
    const calculated = calculateStockRulePrice({
      rule: row?.price?.stockPriceRule,
      referenceCost: row?.referenceCost?.referenceCostRaw ?? row?.referenceCost?.referenceCost,
      settings: priceRecommendationSettings,
    });
    if (calculated.error || calculated.price === null || calculated.price === undefined) {
      return {
        price: null,
        rawPrice: calculated.rawPrice ?? null,
        error: calculated.error || 'invalid_stock_price_rule',
        warning: null,
      };
    }
    const referenceCostValue = Number(row?.referenceCost?.referenceCost);
    return {
      price: Math.max(0, Math.round(calculated.price)),
      rawPrice: calculated.rawPrice,
      error: null,
      warning: Number.isFinite(referenceCostValue) && calculated.price < referenceCostValue
        ? 'price_below_reference_cost'
        : null,
    };
  }, [priceRecommendationSettings]);

  const pdfModalMatchingRows = useMemo(() => {
    if (!pdfModal.isOpen) return [];
    const normalizedSearch = pdfModal.search.trim().toLowerCase();
    const categoryFilters = pdfModal.includeAllCategories
      ? null
      : new Set((pdfModal.categoryIds || []).map((categoryId) => String(categoryId)));
    const clientTypeFilter = pdfModal.clientType !== "all" ? String(pdfModal.clientType) : null;

    return rows.filter((row) => {
      if (categoryFilters && !categoryFilters.has(String(row.categoryId))) return false;
      if (clientTypeFilter && row.clientType !== clientTypeFilter) return false;
      if (!pdfModal.includeSoldOut && row.price?.isSoldOut) return false;
      if (normalizedSearch) {
        const productName = String(row.product?.productName || "").toLowerCase();
        const varietyName = String(row.variety?.varietyName || "").toLowerCase();
        if (!productName.includes(normalizedSearch) && !varietyName.includes(normalizedSearch)) {
          return false;
        }
      }
      return true;
    });
  }, [
    pdfModal.categoryIds,
    pdfModal.clientType,
    pdfModal.includeAllCategories,
    pdfModal.includeSoldOut,
    pdfModal.isOpen,
    pdfModal.search,
    rows,
  ]);

  const updatePriceValue = (priceId, priceValue) => {
    setProducts((prev) =>
      prev.map((product) => ({
        ...product,
        Varieties: (product.Varieties || []).map((variety) => ({
          ...variety,
          VarietyPrices: (variety.VarietyPrices || []).map((price) =>
            price.id === priceId ? { ...price, price: priceValue } : price
          ),
        })),
      }))
    );
  };

  const updatePriceSoldOut = (priceId, nextSoldOut) => {
    setProducts((prev) =>
      prev.map((product) => ({
        ...product,
        Varieties: (product.Varieties || []).map((variety) => ({
          ...variety,
          VarietyPrices: (variety.VarietyPrices || []).map((price) =>
            price.id === priceId ? { ...price, isSoldOut: nextSoldOut } : price
          ),
        })),
      }))
    );
  };

  const handlePriceChange = (priceId, value) => {
    setDraftPrices((prev) => ({
      ...prev,
      [priceId]: value,
    }));
  };

  const toggleProductGroup = (groupKey, variantGroups = []) => {
    const variantKeys = new Set(variantGroups.map((group) => group.key));
    const isCurrentlyExpanded = !!expandedProductKeys[groupKey];
    setExpandedProductKeys(isCurrentlyExpanded ? {} : { [groupKey]: true });
    setExpandedVariantKeys((prev) => {
      if (isCurrentlyExpanded) return {};
      const next = {};
      Object.keys(prev).forEach((variantKey) => {
        if (variantKeys.has(variantKey)) {
          next[variantKey] = true;
        }
      });
      return next;
    });
  };

  const toggleVariantGroup = (groupKey) => {
    setExpandedVariantKeys((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleProductRowKeyDown = (event, groupKey, variantGroups) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    toggleProductGroup(groupKey, variantGroups);
  };

  const handleVariantRowKeyDown = (event, groupKey) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    toggleVariantGroup(groupKey);
  };

  const handleSavePrice = async (price) => {
    if (!canUpdateProducts || !branchId) return;
    const draft = draftPrices[price.id];
    const resolved = normalizePriceValue(draft);
    if (resolved === "") {
      setError("El precio es obligatorio.");
      return;
    }
    const parsed = Number(resolved);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError("El precio debe ser un numero valido.");
      return;
    }
    clearSavedToastTimer();
    setShowSavedToast(false);
    saveCountRef.current += 1;
    setActiveSaveCount(saveCountRef.current);
    setSavingPrices((prev) => ({ ...prev, [price.id]: true }));
    setError("");
    try {
      await updateVarietyPrice(branchId, price.id, { price: parsed });
      updatePriceValue(price.id, parsed);
      setDraftPrices((prev) => {
        const next = { ...prev };
        delete next[price.id];
        return next;
      });
    } catch (err) {
      console.error("Error updating price:", err);
      setError("No se pudo actualizar el precio.");
    } finally {
      setSavingPrices((prev) => ({ ...prev, [price.id]: false }));
      saveCountRef.current = Math.max(saveCountRef.current - 1, 0);
      setActiveSaveCount(saveCountRef.current);
      if (saveCountRef.current === 0) {
        triggerSavedToast();
      }
    }
  };

  const handleApplyRecommendedPrice = async (row) => {
    if (!canUpdateProducts || !branchId) return;
    const price = row?.price;
    if (!price?.id) return;
    const recommendation = getPriceRecommendationForRow(row);
    if (recommendation.price === null || recommendation.price === undefined) {
      setError("Este precio no tiene una regla y costo de referencia validos.");
      return;
    }
    if (Number(price.price) === Number(recommendation.price)) {
      setDraftPrices((prev) => {
        const next = { ...prev };
        delete next[price.id];
        return next;
      });
      return;
    }

    clearSavedToastTimer();
    setShowSavedToast(false);
    saveCountRef.current += 1;
    setActiveSaveCount(saveCountRef.current);
    setSavingPrices((prev) => ({ ...prev, [price.id]: true }));
    setError("");
    try {
      await updateVarietyPrice(branchId, price.id, { price: recommendation.price });
      updatePriceValue(price.id, recommendation.price);
      setDraftPrices((prev) => {
        const next = { ...prev };
        delete next[price.id];
        return next;
      });
    } catch (err) {
      console.error("Error applying recommended price:", err);
      setError("No se pudo aplicar el precio recomendado.");
    } finally {
      setSavingPrices((prev) => ({ ...prev, [price.id]: false }));
      saveCountRef.current = Math.max(saveCountRef.current - 1, 0);
      setActiveSaveCount(saveCountRef.current);
      if (saveCountRef.current === 0) {
        triggerSavedToast();
      }
    }
  };

  const handleToggleSoldOut = async (price) => {
    if (!canUpdateProducts || !branchId) return;
    const nextSoldOut = !price.isSoldOut;
    setSavingSoldOut((prev) => ({ ...prev, [price.id]: true }));
    setError("");
    try {
      if (nextSoldOut) {
        await markPriceAsSold(branchId, price.id);
      } else {
        await markPriceAsAvailable(branchId, price.id);
      }
      updatePriceSoldOut(price.id, nextSoldOut);
    } catch (err) {
      console.error("Error updating sold out state:", err);
      setError("No se pudo actualizar el estado de agotado.");
    } finally {
      setSavingSoldOut((prev) => ({ ...prev, [price.id]: false }));
    }
  };

  const handleOpenPdfModal = () => {
    const allCategoryIds = categoryOptions.map((category) => String(category.id));
    const includeAllCategories = selectedCategory === "all";
    const selectedCategoryIds = includeAllCategories
      ? allCategoryIds
      : [selectedCategory].map((categoryId) => String(categoryId)).filter(Boolean);

    setError("");
    setPdfModal({
      ...createInitialPdfModalState(),
      isOpen: true,
      search: searchTerm,
      clientType: selectedClientType,
      categoryIds: selectedCategoryIds,
      includeAllCategories,
      includeSoldOut: getDefaultPdfIncludeSoldOut(selectedClientType),
    });
  };

  const handleClosePdfModal = () => {
    if (pdfLoading) return;
    setPdfModal(createInitialPdfModalState());
  };

  const handlePdfModalClientTypeChange = (clientType) => {
    setPdfModal((prev) => ({
      ...prev,
      clientType,
      includeSoldOut: getDefaultPdfIncludeSoldOut(clientType),
      error: "",
      shareUrl: "",
      downloadUrl: "",
      jobId: "",
      copyStatus: "",
    }));
  };

  const handleTogglePdfModalCategory = (categoryId) => {
    const normalizedId = String(categoryId);
    setPdfModal((prev) => {
      const currentIds = new Set((prev.categoryIds || []).map((id) => String(id)));
      if (currentIds.has(normalizedId)) {
        currentIds.delete(normalizedId);
      } else {
        currentIds.add(normalizedId);
      }
      const nextCategoryIds = Array.from(currentIds);
      const includeAllCategories =
        categoryOptions.length > 0 && nextCategoryIds.length === categoryOptions.length;
      return {
        ...prev,
        categoryIds: includeAllCategories
          ? categoryOptions.map((category) => String(category.id))
          : nextCategoryIds,
        includeAllCategories,
        error: "",
        shareUrl: "",
        downloadUrl: "",
        jobId: "",
        copyStatus: "",
      };
    });
  };

  const handleSelectAllPdfModalCategories = () => {
    setPdfModal((prev) => ({
      ...prev,
      categoryIds: categoryOptions.map((category) => String(category.id)),
      includeAllCategories: true,
      error: "",
      shareUrl: "",
      downloadUrl: "",
      jobId: "",
      copyStatus: "",
    }));
  };

  const handleClearPdfModalCategories = () => {
    setPdfModal((prev) => ({
      ...prev,
      categoryIds: [],
      includeAllCategories: false,
      error: "",
      shareUrl: "",
      downloadUrl: "",
      jobId: "",
      copyStatus: "",
    }));
  };

  const handleGeneratePdf = async () => {
    if (!canReadProducts) return;
    if (!branchId) return;
    const categoryIds = (pdfModal.categoryIds || []).map((categoryId) => String(categoryId));
    if (!pdfModal.includeAllCategories && !categoryIds.length) {
      setPdfModal((prev) => ({ ...prev, error: "Selecciona al menos una categoria." }));
      return;
    }
    if (!pdfModalMatchingRows.length) {
      setPdfModal((prev) => ({ ...prev, error: "No hay resultados para exportar." }));
      return;
    }
    setPdfLoading(true);
    try {
      setError("");
      const trimmedSearch = pdfModal.search.trim();
      const selectedCategoryIds = pdfModal.includeAllCategories ? [] : categoryIds;
      clearPendingPdfJob();
      setPdfModal((prev) => ({
        ...prev,
        error: "",
        shareUrl: "",
        downloadUrl: "",
        jobId: "",
        copyStatus: "",
      }));
      const result = await requestPriceListPdfJob(branchId, {
        clientType: pdfModal.clientType,
        category: pdfModal.includeAllCategories ? "all" : selectedCategoryIds[0] || "all",
        categories: selectedCategoryIds,
        search: trimmedSearch || undefined,
        includeImages: 1,
        includeSoldOut: pdfModal.includeSoldOut,
      });
      const jobId = result?.jobId;
      if (!jobId) {
        throw new Error("Missing PDF job id");
      }
      if (result?.status === "ready") {
        await downloadPdfByJob(jobId);
        return;
      }
      pendingPdfJobRef.current = jobId;
      startPdfPolling(jobId);
    } catch (err) {
      console.error("Error generating PDF:", err);
      setError("No se pudo generar el PDF.");
      setPdfModal((prev) => ({ ...prev, error: "No se pudo generar el PDF." }));
      setPdfLoading(false);
      clearPendingPdfJob();
    }
  };

  const handleCopyShareLink = async () => {
    if (!pdfModal.shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pdfModal.shareUrl);
      } else {
        const input = document.createElement("textarea");
        input.value = pdfModal.shareUrl;
        input.setAttribute("readonly", "");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      setPdfModal((prev) => ({ ...prev, copyStatus: "Copiado" }));
    } catch (err) {
      console.error("Error copying PDF link:", err);
      setPdfModal((prev) => ({ ...prev, copyStatus: "No se pudo copiar" }));
    }
  };

  const renderPriceDetailRows = (priceRows) =>
    priceRows.map((row) => {
      const { price, referenceCost, clientTypeLabel } = row;
      const rowIsSoldOut = price.isSoldOut;
      const rowOpacity = rowIsSoldOut ? "opacity-70" : "";
      const draftValue =
        draftPrices[price.id] !== undefined
          ? draftPrices[price.id]
          : normalizePriceValue(price.price);
      const isDirty = normalizePriceValue(price.price) !== normalizePriceValue(draftValue);
      const isSaving = savingPrices[price.id];
      const isToggling = savingSoldOut[price.id];
      const referenceCostValue = Number(referenceCost?.referenceCost);
      const currentDraftValue = Number(draftValue);
      const isBelowReferenceCost =
        Number.isFinite(referenceCostValue)
        && Number.isFinite(currentDraftValue)
        && currentDraftValue < referenceCostValue;
      const recommendation = getPriceRecommendationForRow(row);
      const hasRecommendedPrice = recommendation.price !== null && recommendation.price !== undefined;
      const recommendedIsCurrent = hasRecommendedPrice
        && Number(price.price) === Number(recommendation.price);
      const recommendedIsBelowReferenceCost = recommendation.warning === 'price_below_reference_cost';
      const recommendationLabel = recommendation.error === 'missing_stock_price_rule'
        ? 'Sin regla'
        : recommendation.error === 'missing_reference_cost'
          ? 'Sin costo'
          : 'Sin recomendacion';

      return (
        <tr key={price.id} className={isBelowReferenceCost || recommendedIsBelowReferenceCost ? "bg-amber-50" : rowIsSoldOut ? "bg-amber-50" : "bg-white"}>
          <td className={`px-3 py-2 text-sm font-medium ${rowOpacity}`}>{clientTypeLabel || "-"}</td>
          <td className={`px-3 py-2 text-sm ${rowOpacity}`}>{price.metricUnit || "-"}</td>
          <td className={`px-3 py-2 text-sm ${rowOpacity}`}>
            {canUpdateProducts ? (
              <input
                type="number"
                className={`w-24 border rounded px-2 py-1 text-sm ${
                  isBelowReferenceCost ? "border-amber-400 bg-amber-50" : "border-gray-300"
                }`}
                value={draftValue}
                onChange={(event) => handlePriceChange(price.id, event.target.value)}
                min="0"
                disabled={isSaving}
                onBlur={() => {
                  if (!isDirty || isSaving) return;
                  handleSavePrice(price);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  if (!isDirty || isSaving) return;
                  handleSavePrice(price);
                }}
              />
            ) : (
              <span className={isBelowReferenceCost ? "font-semibold text-amber-800" : ""}>
                {formatPriceLabel(price.price)}
              </span>
            )}
            {isBelowReferenceCost && (
              <span className="block mt-1 text-[11px] font-semibold text-amber-700">
                Bajo costo ref.
              </span>
            )}
          </td>
          <td
            className={`px-3 py-2 text-sm ${rowOpacity}`}
            title={buildReferenceCostTitle(referenceCost)}
          >
            {renderReferenceCostLabel(referenceCost)}
          </td>
          <td className={`px-3 py-2 text-sm ${rowOpacity}`}>
            {hasRecommendedPrice ? (
              <div className="flex flex-col gap-1">
                <span className={recommendedIsBelowReferenceCost ? "font-semibold text-amber-800" : "font-semibold text-gray-900"}>
                  {formatPriceLabel(recommendation.price)}
                </span>
                {recommendedIsBelowReferenceCost && (
                  <span className="text-[11px] font-semibold text-amber-700">Bajo costo ref.</span>
                )}
                {canUpdateProducts && !recommendedIsCurrent && (
                  <button
                    type="button"
                    className="w-fit rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    disabled={isSaving}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleApplyRecommendedPrice(row)}
                  >
                    Aplicar
                  </button>
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-400">{recommendationLabel}</span>
            )}
          </td>
          <td className={`px-3 py-2 text-sm ${rowOpacity}`}>
            {canUpdateProducts ? (
              <button
                type="button"
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  rowIsSoldOut
                    ? "bg-red-100 text-red-700 border-red-200"
                    : "bg-emerald-100 text-emerald-700 border-emerald-200"
                }`}
                disabled={isToggling}
                onClick={() => handleToggleSoldOut(price)}
              >
                {isToggling ? "Actualizando..." : rowIsSoldOut ? "Agotado" : "Disponible"}
              </button>
            ) : (
              <span className={rowIsSoldOut ? "text-red-700" : "text-emerald-700"}>
                {rowIsSoldOut ? "Agotado" : "Disponible"}
              </span>
            )}
          </td>
        </tr>
      );
    });

  const renderVariantGroups = (variantGroups) =>
    variantGroups.map((group, index) => {
      const { product, variety, priceRows, productSoldOut } = group;
      const firstPrice = priceRows[0]?.price || {};
      const imageSrc =
        firstPrice.priceImage ||
        variety?.varietyImage ||
        product?.productImage ||
        FALLBACK_IMAGE;
      const isExpanded = !!expandedVariantKeys[group.key];
      const detailId = `variant-prices-${String(group.key).replace(/[^A-Za-z0-9_-]/g, "-")}`;
      const varietyLabel = stripVarietyPrefix(product?.productName, variety?.varietyName);
      const unitLabels = getUniqueLabels(priceRows.map((row) => row.price?.metricUnit));
      const unitLabel = unitLabels.length > 1 ? `${unitLabels.length} unidades` : unitLabels[0] || "-";
      const availabilityLabel = getVariantAvailabilityLabel(priceRows);
      const availabilityClass = getVariantAvailabilityClass(availabilityLabel);
      const summaryOpacity = availabilityLabel === "Agotada" ? "opacity-70" : "";
      const zebraTone = index % 2 === 0 ? "bg-white" : "bg-slate-50";
      const rowTone = availabilityLabel === "Agotada" ? "bg-amber-50" : zebraTone;
      const priceCountLabel = priceRows.length === 1 ? "1 precio" : `${priceRows.length} precios`;

      return (
        <React.Fragment key={group.key}>
          <tr
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            aria-controls={detailId}
            onClick={() => toggleVariantGroup(group.key)}
            onKeyDown={(event) => handleVariantRowKeyDown(event, group.key)}
            className={`border-b cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 hover:bg-blue-50 ${rowTone}`}
          >
            <td className={`px-2 py-2 ${summaryOpacity}`}>
              <div className="flex items-start gap-2">
                <img
                  src={imageSrc}
                  alt={varietyLabel || product?.productName || "Variedad"}
                  className="h-10 w-10 rounded object-cover border border-gray-200 flex-none"
                />
                <div className="min-w-0">
                  <div className="font-semibold text-sm leading-tight break-words">{varietyLabel || "-"}</div>
                  {productSoldOut && (
                    <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 mt-1">
                      Producto agotado
                    </span>
                  )}
                </div>
              </div>
            </td>
            <td className={`px-2 py-2 text-sm ${summaryOpacity}`}>{unitLabel}</td>
            <td className={`px-2 py-2 text-sm ${summaryOpacity}`}>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700">
                {priceCountLabel}
              </span>
            </td>
            <td className={`px-2 py-2 text-sm ${summaryOpacity}`}>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${availabilityClass}`}>
                {availabilityLabel}
              </span>
            </td>
          </tr>
          {isExpanded && (
            <tr id={detailId} className="border-b bg-slate-100">
              <td colSpan={4} className="px-2 py-3">
                <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                  <table className="min-w-[760px] w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2">Unidad</th>
                        <th className="px-3 py-2">Precio</th>
                        <th className="px-3 py-2">Costo ref.</th>
                        <th className="px-3 py-2">Recomendado</th>
                        <th className="px-3 py-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {renderPriceDetailRows(priceRows)}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });

  const renderProductGroups = () =>
    filteredProductGroups.map((group, index) => {
      const { product, variantGroups, priceRows } = group;
      const firstVariant = variantGroups[0]?.variety;
      const firstPrice = priceRows[0]?.price || {};
      const imageSrc =
        product?.productImage ||
        firstVariant?.varietyImage ||
        firstPrice.priceImage ||
        FALLBACK_IMAGE;
      const isExpanded = !!expandedProductKeys[group.key];
      const detailId = `product-variants-${String(group.key).replace(/[^A-Za-z0-9_-]/g, "-")}`;
      const availabilityLabel = getVariantAvailabilityLabel(priceRows, "Agotado");
      const availabilityClass = getVariantAvailabilityClass(availabilityLabel);
      const summaryOpacity = availabilityLabel === "Agotado" ? "opacity-70" : "";
      const zebraTone = index % 2 === 0 ? "bg-white" : "bg-slate-50";
      const rowTone = availabilityLabel === "Agotado" ? "bg-amber-50" : zebraTone;
      const variantCountLabel =
        variantGroups.length === 1 ? "1 variedad" : `${variantGroups.length} variedades`;
      const priceCountLabel = priceRows.length === 1 ? "1 precio" : `${priceRows.length} precios`;

      return (
        <React.Fragment key={group.key}>
          <tr
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            aria-controls={detailId}
            onClick={() => toggleProductGroup(group.key, variantGroups)}
            onKeyDown={(event) => handleProductRowKeyDown(event, group.key, variantGroups)}
            className={`border-b cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 hover:bg-blue-50 ${rowTone}`}
          >
            <td className={`px-2 py-2 ${summaryOpacity}`}>
              <div className="flex items-start gap-2">
                <img
                  src={imageSrc}
                  alt={product?.productName || "Producto"}
                  className="h-10 w-10 rounded object-cover border border-gray-200 flex-none"
                />
                <div className="min-w-0">
                  <div className="font-semibold text-sm leading-tight break-words">{product?.productName || "-"}</div>
                </div>
              </div>
            </td>
            <td className={`px-2 py-2 text-sm ${summaryOpacity}`}>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700">
                {variantCountLabel}
              </span>
            </td>
            <td className={`px-2 py-2 text-sm ${summaryOpacity}`}>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700">
                {priceCountLabel}
              </span>
            </td>
            <td className={`px-2 py-2 text-sm ${summaryOpacity}`}>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${availabilityClass}`}>
                {availabilityLabel}
              </span>
            </td>
          </tr>
          {isExpanded && (
            <tr id={detailId} className="border-b bg-slate-100">
              <td colSpan={4} className="px-2 py-3">
                <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                  <table className="min-w-[560px] w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-2 py-2">Variedad</th>
                        <th className="px-2 py-2">Unidad</th>
                        <th className="px-2 py-2">Precios</th>
                        <th className="px-2 py-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderVariantGroups(variantGroups)}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-gray-600">
        <InlineLoading label="Cargando lista de precios" />
      </div>
    );
  }

  if (error && !rows.length) {
    return <div className="p-6 text-center text-sm text-gray-600">{error}</div>;
  }

  if (!rows.length) {
    return <div className="p-6 text-center text-sm text-gray-600">No hay precios registrados.</div>;
  }

  const handleBackToSettings = () => {
    navigate("/settings?topic=catalog&section=catalog-price-list");
  };
  const pdfModalCategoryIdSet = new Set((pdfModal.categoryIds || []).map((categoryId) => String(categoryId)));
  const selectedPdfCategoryCount = pdfModal.includeAllCategories
    ? categoryOptions.length
    : pdfModalCategoryIdSet.size;
  const pdfModalResultCount = pdfModal.isOpen ? pdfModalMatchingRows.length : 0;

  return (
    <div className={embedded ? "min-w-0 space-y-4" : "p-6 bg-gray-100 min-h-screen"}>
      <div className={embedded ? "min-w-0 space-y-4" : "max-w-6xl mx-auto space-y-4"}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className={embedded ? "text-xl font-bold" : "text-2xl font-bold"}>
              Lista de precios
            </h1>
            <p className="text-sm text-gray-600">
              Actualiza precios y disponibilidad por producto y variedad desde una sola tabla.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!embedded && (
              <button
                type="button"
                onClick={handleBackToSettings}
                className="px-3 py-2 rounded border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                Volver a configuracion
              </button>
            )}
            {embedded && (
              <button
                type="button"
                onClick={onOpenFullPage || (() => navigate(`/branches/${branchId}/price-list`))}
                className="px-3 py-2 rounded border border-blue-200 text-sm font-semibold text-blue-700 hover:bg-blue-50"
              >
                Ver pantalla completa
              </button>
            )}
            <button
              type="button"
              onClick={handleOpenPdfModal}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300"
              disabled={pdfLoading}
            >
              {pdfLoading ? <InlineLoading label="Generando PDF" /> : "Descargar PDF"}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar producto o variedad..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="flex flex-wrap items-center gap-2">
            {CLIENT_TYPE_FILTERS.map((option) => {
              const isSelected = selectedClientType === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedClientType(option.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                    isSelected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <CategoryFilterBar
            categories={categoryOptions}
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
            includeAll
            allItem={{ id: "all", label: "Todas", initial: "T" }}
            itemsWrapperClassName="pb-2"
            emptyMessage="No hay categorias disponibles."
            isEmpty={categoryOptions.length === 0}
          />
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="max-w-full overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-[720px] w-full text-left text-sm" id="price-list-table">
            <thead className="bg-gray-100 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-2 py-2">Producto</th>
                <th className="px-2 py-2">Variedades</th>
                <th className="px-2 py-2">Precios</th>
                <th className="px-2 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filteredProductGroups.length ? (
                renderProductGroups()
              ) : (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-gray-500">
                    No hay resultados para los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pdfModal.isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-[1200] flex items-start justify-center px-4 py-6 overflow-y-auto"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-lg shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Generar PDF</h2>
                <p className="text-sm text-gray-600">
                  Confirma los filtros antes de compartir la lista de precios.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClosePdfModal}
                disabled={pdfLoading}
                className="rounded-full px-2 py-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                aria-label="Cerrar"
              >
                X
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Busqueda
                </label>
                <input
                  type="text"
                  value={pdfModal.search}
                  onChange={(event) => setPdfModal((prev) => ({
                    ...prev,
                    search: event.target.value,
                    error: "",
                    shareUrl: "",
                    downloadUrl: "",
                    jobId: "",
                    copyStatus: "",
                  }))}
                  placeholder="Buscar producto o variedad..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={pdfLoading}
                />
              </div>

              <div>
                <p className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de cliente
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {CLIENT_TYPE_FILTERS.map((option) => {
                    const isSelected = pdfModal.clientType === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handlePdfModalClientTypeChange(option.id)}
                        disabled={pdfLoading}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition disabled:opacity-60 ${
                          isSelected
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                  <p className="block text-sm font-medium text-gray-700">
                    Categorias
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllPdfModalCategories}
                      disabled={pdfLoading}
                      className="px-2.5 py-1 rounded border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      Todas
                    </button>
                    <button
                      type="button"
                      onClick={handleClearPdfModalCategories}
                      disabled={pdfLoading}
                      className="px-2.5 py-1 rounded border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>
                {categoryOptions.length ? (
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 p-2">
                    <div className="flex flex-wrap gap-2">
                      {categoryOptions.map((category) => {
                        const isSelected = pdfModal.includeAllCategories || pdfModalCategoryIdSet.has(String(category.id));
                        return (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => handleTogglePdfModalCategory(category.id)}
                            disabled={pdfLoading}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition disabled:opacity-60 ${
                              isSelected
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {category.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">
                    No hay categorias disponibles.
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {pdfModal.includeAllCategories
                    ? "Todas las categorias incluidas"
                    : `${selectedPdfCategoryCount} categorias seleccionadas`}
                </p>
              </div>

              <label className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={pdfModal.includeSoldOut}
                  onChange={(event) => setPdfModal((prev) => ({
                    ...prev,
                    includeSoldOut: event.target.checked,
                    error: "",
                    shareUrl: "",
                    downloadUrl: "",
                    jobId: "",
                    copyStatus: "",
                  }))}
                  disabled={pdfLoading}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block font-medium text-gray-900">Mostrar agotados</span>
                  <span className="block text-xs text-gray-500">
                    Incluye variedades marcadas como agotadas en esta sucursal.
                  </span>
                </span>
              </label>

              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
                {pdfModalResultCount} filas coinciden con estos filtros.
              </div>

              {pdfModal.error && (
                <div className="text-sm text-red-600">{pdfModal.error}</div>
              )}

              {pdfModal.shareUrl && (
                <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">PDF listo para compartir</p>
                    <p className="text-xs text-emerald-800">
                      El enlace publico expira junto con el archivo generado.
                    </p>
                  </div>
                  <input
                    type="text"
                    value={pdfModal.shareUrl}
                    readOnly
                    className="w-full rounded border border-emerald-200 bg-white px-3 py-2 text-xs text-gray-700"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopyShareLink}
                      className="px-3 py-2 rounded bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800"
                    >
                      Copiar link
                    </button>
                    <a
                      href={pdfModal.shareUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-2 rounded border border-emerald-300 bg-white text-emerald-800 text-sm font-semibold hover:bg-emerald-100"
                    >
                      Abrir link
                    </a>
                    <a
                      href={pdfModal.downloadUrl}
                      className="px-3 py-2 rounded border border-emerald-300 bg-white text-emerald-800 text-sm font-semibold hover:bg-emerald-100"
                    >
                      Descargar
                    </a>
                    {pdfModal.copyStatus && (
                      <span className="text-xs text-emerald-800">{pdfModal.copyStatus}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClosePdfModal}
                disabled={pdfLoading}
                className="px-4 py-2 rounded border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={pdfLoading}
                className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300"
              >
                {pdfLoading ? <InlineLoading label="Generando PDF" /> : "Generar y descargar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {(activeSaveCount > 0 || showSavedToast) && (
        <div className="fixed bottom-4 right-4 z-50" aria-live="polite">
          <div
            className={`rounded-xl shadow-lg px-4 py-3 text-sm text-white ${
              activeSaveCount > 0 ? "bg-slate-800" : "bg-emerald-600"
            }`}
          >
            {activeSaveCount > 0 ? "Guardando..." : "Guardado"}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPriceListPage;
